import React, { useState, useEffect } from 'react';
import { ChevronDown, Plus, Zap, AlertCircle, Check, Trash2, Link2, Unlink2, RotateCw, Copy } from 'lucide-react';
import { safeAsync, Badge, KPI, neuShadow, neuShadowSm, neuShadowInset, inp, btnPrimary, btnSm, btnDanger, cardWrap, emptyMsg, overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn } from './crmShared';
const api = () => window.zignagenixAPI;

const WEBHOOK_EVENTS = [
  { id: 'contact.created', label: 'Contact Created' },
  { id: 'contact.updated', label: 'Contact Updated' },
  { id: 'opportunity.won', label: 'Opportunity Won' },
  { id: 'opportunity.lost', label: 'Opportunity Lost' },
  { id: 'form.submitted', label: 'Form Submitted' },
  { id: 'appointment.booked', label: 'Appointment Booked' },
  { id: 'invoice.paid', label: 'Invoice Paid' },
  { id: 'payment.received', label: 'Payment Received' },
  { id: 'task.completed', label: 'Task Completed' },
  { id: 'review.received', label: 'Review Received' },
];

const INTEGRATIONS = [
  { id: 'stripe', name: 'Stripe', icon: '💳' },
  { id: 'google', name: 'Google', icon: '🔍' },
  { id: 'facebook', name: 'Facebook', icon: 'f' },
  { id: 'zapier', name: 'Zapier', icon: '⚡' },
  { id: 'mailchimp', name: 'Mailchimp', icon: '📧' },
  { id: 'quickbooks', name: 'QuickBooks', icon: '📊' },
];

export default function WebhooksTab({ canCreate, canUpdate }) {
  const [tab, setTab] = useState('webhooks');
  const [webhooks, setWebhooks] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    safeAsync(async () => {
      setLoading(true);
      const [wh, ig] = await Promise.all([api().getCRMWebhooks(), api().getCRMIntegrations()]);
      // Defensive: coerce truthy non-arrays to [] so the subsequent
      // .map() / .filter() / .reduce() calls don't crash with
      // "x.filter is not a function" / "x.map is not a function".
      setWebhooks(Array.isArray(wh) ? wh : []);
      setIntegrations(Array.isArray(ig) ? ig : []);
      setLoading(false);
    })();
  }, []);

  const openWebhookModal = (wh) => {
    setForm(wh || { name: '', url: '', events: [], secret: '', retryCount: 3 });
    setTestResult(null);
    setModal('webhook');
  };

  const saveWebhook = safeAsync(async () => {
    if (!form.name || !form.url) return alert('Name and URL required');
    if (form.id) {
      await api().updateCRMWebhook(form.id, form);
      setWebhooks(webhooks.map(w => w.id === form.id ? form : w));
    } else {
      const res = await api().createCRMWebhook(form);
      setWebhooks([...webhooks, res]);
    }
    setModal(null);
  });

  const deleteWebhook = safeAsync(async (id) => {
    if (!window.confirm('Delete webhook?')) return;
    await api().deleteCRMWebhook(id);
    setWebhooks(webhooks.filter(w => w.id !== id));
  });

  const testWebhook = safeAsync(async (id) => {
    const res = await api().testCRMWebhook(id);
    setTestResult(res);
  });

  const toggleWebhookActive = safeAsync(async (wh) => {
    const updated = { ...wh, active: !wh.active };
    await api().updateCRMWebhook(wh.id, updated);
    setWebhooks(webhooks.map(w => w.id === wh.id ? updated : w));
  });

  const toggleIntegration = safeAsync(async (ig) => {
    const isConnected = ig.status === 'connected';
    if (isConnected) {
      await api().deleteCRMIntegration(ig.id);
      setIntegrations(integrations.map(i => i.id === ig.id ? { ...i, status: 'disconnected' } : i));
    } else {
      const res = await api().createCRMIntegration({ providerId: ig.id });
      setIntegrations(integrations.map(i => i.id === ig.id ? res : i));
    }
  });

  const stats = {
    activeWebhooks: webhooks.filter(w => w.active).length,
    totalTriggers: webhooks.reduce((s, w) => s + (w.triggerCount || 0), 0),
    successRate: webhooks.length > 0 ? Math.round(webhooks.reduce((s, w) => s + (w.successRate || 0), 0) / webhooks.length) : 0,
    connectedIntegrations: integrations.filter(i => i.status === 'connected').length,
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, borderBottom: '1px solid #E9ECEF', paddingBottom: 12 }}>
        {['webhooks', 'integrations'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 16px', border: 'none', background: tab === t ? '#2E8B98' : 'transparent', color: tab === t ? '#fff' : '#6C757D', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.15s' }}>
            {t === 'webhooks' ? 'Webhooks' : 'Integrations'}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <KPI icon={Zap} label="Active Webhooks" value={stats.activeWebhooks} color="#F26522" />
        <KPI icon={Zap} label="Total Triggers" value={stats.totalTriggers} color="#62BB46" />
        <KPI icon={Check} label="Success Rate" value={`${stats.successRate}%`} color="#2E8B98" />
        <KPI icon={Link2} label="Connected" value={stats.connectedIntegrations} color="#256F7A" />
      </div>

      {tab === 'webhooks' && (
        <div>
          {canCreate && (
            <button onClick={() => openWebhookModal()} style={{ ...btnPrimary, marginBottom: 20 }}>
              <Plus size={16} /> New Webhook
            </button>
          )}

          {loading ? <div style={emptyMsg}>Loading...</div> : webhooks.length === 0 ? (
            <div style={emptyMsg}>No webhooks configured. Create one to start receiving events.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {webhooks.map(wh => (
                <div key={wh.id} style={{ ...cardWrap, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#212529' }}>{wh.name}</h4>
                      <Badge text={wh.active ? 'Active' : 'Inactive'} color={wh.active ? '#62BB46' : '#ADB5BD'} />
                    </div>
                    <div style={{ fontSize: 12, color: '#6C757D', marginBottom: 8, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{wh.url}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                      {wh.events.slice(0, 3).map(e => (
                        <Badge key={e} text={WEBHOOK_EVENTS.find(ev => ev.id === e)?.label || e} color="#2E8B98" />
                      ))}
                      {wh.events.length > 3 && <Badge text={`+${wh.events.length - 3}`} color="#6C757D" />}
                    </div>
                    <div style={{ fontSize: 11, color: '#ADB5BD' }}>Last: {wh.lastTriggered ? new Date(wh.lastTriggered).toLocaleString() : 'Never'} | Success: {wh.successRate || 0}%</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button onClick={() => toggleWebhookActive(wh)} style={{ ...btnSm, background: wh.active ? '#E8F5E9' : '#FCE4EC' }} title={wh.active ? 'Deactivate' : 'Activate'}>
                      {wh.active ? '✓' : '○'}
                    </button>
                    <button onClick={() => testWebhook(wh.id)} style={btnSm} title="Test">
                      <RotateCw size={14} />
                    </button>
                    {canUpdate && <button onClick={() => openWebhookModal(wh)} style={btnSm}>Edit</button>}
                    <button onClick={() => deleteWebhook(wh.id)} style={{ ...btnDanger }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'integrations' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
          {INTEGRATIONS.map(ig => {
            const data = integrations.find(i => i.id === ig.id) || { id: ig.id, status: 'disconnected' };
            const isConnected = data.status === 'connected';
            return (
              <div key={ig.id} style={{ ...cardWrap, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <div style={{ fontSize: 18, marginBottom: 4 }}>{ig.icon}</div>
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#212529' }}>{ig.name}</h4>
                  </div>
                  <Badge text={data.status === 'error' ? 'Error' : isConnected ? 'Connected' : 'Disconnected'} color={data.status === 'error' ? '#DC3545' : isConnected ? '#62BB46' : '#ADB5BD'} />
                </div>
                {data.lastSync && <div style={{ fontSize: 11, color: '#ADB5BD' }}>Synced: {new Date(data.lastSync).toLocaleString()}</div>}
                <button onClick={() => toggleIntegration(data)} style={{ ...btnPrimary, width: '100%', justifyContent: 'center' }}>
                  {isConnected ? <Unlink2 size={14} /> : <Link2 size={14} />}
                  {isConnected ? 'Disconnect' : 'Connect'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {modal === 'webhook' && (
        <div style={overlayStyle} onClick={() => setModal(null)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader}>
              <h2 style={modalTitle}>{form.id ? 'Edit Webhook' : 'New Webhook'}</h2>
              <button onClick={() => setModal(null)} style={closeBtn}>✕</button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>Name</label>
              <input type="text" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inp} placeholder="e.g., CRM Sync" />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>URL</label>
              <input type="url" value={form.url || ''} onChange={(e) => setForm({ ...form, url: e.target.value })} style={inp} placeholder="https://..." />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#212529', marginBottom: 8, display: 'block' }}>Events</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {WEBHOOK_EVENTS.map(e => (
                  <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}>
                    <input type="checkbox" checked={(form.events || []).includes(e.id)} onChange={(ev) => {
                      const events = form.events || [];
                      setForm({ ...form, events: ev.target.checked ? [...events, e.id] : events.filter(x => x !== e.id) });
                    }} style={{ cursor: 'pointer' }} />
                    {e.label}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>Secret Key</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="text" value={form.secret || ''} onChange={(e) => setForm({ ...form, secret: e.target.value })} style={{ ...inp, flex: 1, fontFamily: 'monospace' }} placeholder="auto-generated" readOnly />
                <button onClick={() => {
                  const key = Math.random().toString(36).substr(2, 32);
                  setForm({ ...form, secret: key });
                }} style={btnSm}><Copy size={14} /></button>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>Retry Count</label>
              <input type="number" value={form.retryCount || 3} onChange={(e) => setForm({ ...form, retryCount: parseInt(e.target.value) })} style={inp} min="0" max="10" />
            </div>

            {testResult && (
              <div style={{ background: testResult.success ? '#E8F5E9' : '#FFEBEE', borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 12, color: testResult.success ? '#2E7D32' : '#C62828' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  {testResult.success ? <Check size={14} /> : <AlertCircle size={14} />}
                  <strong>{testResult.success ? 'Success' : 'Failed'}</strong>
                </div>
                <div>{testResult.message}</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => saveWebhook()} style={{ ...btnPrimary, flex: 1 }}>Save</button>
              {form.id && <button onClick={() => testWebhook(form.id)} style={{ ...btnSm, flex: 1 }}>Test</button>}
              <button onClick={() => setModal(null)} style={{ ...btnSm, flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
